import React, { useState, useMemo } from 'react';
import { track, useTrackScreen } from '@/lib/analytics';
import { getUserMessage } from '@/lib/errors';
import { View, Text, ScrollView } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { ScreenFrame } from '@/components/ScreenFrame';
import {
  Folder,
  Clock,
  CheckCircle,
  ThumbsUp,
  ThumbsDown,
  ChevronRight,
  FileText,
  Calendar,
  TrendingUp,
  Users,
  AlertTriangle,
  BarChart3,
  Target,
  PlayCircle,
  History,
  Pencil,
  Share2,
  Check,
} from 'lucide-react-native';
import Animated, { FadeInUp, FadeIn } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { labelColor, scoreToLabel, capitalizeLabel } from '@/lib/scoring/score-display';
import { mode } from '@/lib/config/app-mode';
import { usePhoneShell } from '@/lib/useShell';
import { AppLayout } from '@/components/AppLayout';
import { useProfileStore } from '@/lib/state/profile-store';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import { useInvestorProfile } from '@/lib/supabase';
import { useMyDataRoomAccessRequests } from '@/lib/supabase/api';
import { selectDDReviews, type DDAccessState } from '@/lib/dd/dd-entry';
import { useCompletedDDReports, useShareDDReport } from '@/lib/dd/use-dd-reports';
import { useGenerateNarrative, useSaveNarrative, type DDNarrativeDraft } from '@/lib/dd/use-dd-narrative';
import { DDNarrativeEditor } from '@/components/investor/dd/DDNarrativeEditor';
import { GenerateNarrativeAction } from '@/components/investor/dd/GenerateNarrativeAction';
import { useAlert } from '@/components/AlertDialog';
import type { DDMetric } from '@/lib/dd/dd-metrics';
import { router } from 'expo-router';
import { SectionDivider } from '@/components/SectionDivider';
import { UsageIndicator } from '@/components/subscription/UsageIndicator';

/**
 * Badge for a review's data-room request state.
 *
 * `granted` keeps the original "In Progress" styling so the happy path looks
 * unchanged; the other two states are new because they were previously
 * unreachable (the DD list could only ever contain granted rows).
 */
function ddAccessBadge(
  state: DDAccessState,
  colors: ReturnType<typeof useThemeColors>,
): { label: string; color: string; bg: string } {
  switch (state) {
    case 'awaiting':
      return { label: 'Awaiting access', color: colors.warning, bg: colors.amber.light };
    case 'declined':
      return { label: 'Access declined', color: colors.text.tertiary, bg: colors.light[300] };
    case 'granted':
    default:
      return { label: 'In Progress', color: colors.warning, bg: colors.amber.light };
  }
}

// Mock completed due diligence data with more details
interface ScoreSnapshot {
  overall: number;
  confidence: number;
  risk: number;
}

interface CompletedDueDiligence {
  id: string;
  startupId: string;
  startupName: string;
  industry: string;
  stage: string;
  decision: 'invest' | 'pass' | 'follow_up';
  startDate: string;
  endDate: string;
  durationDays: number;
  dataRoomVersion: string;
  documentsReviewed: number;
  flaggedIssues: number;
  finalComments: string;
  executiveSummary: string;
  investmentThesis: string;
  keyRisks: string;
  notablePoints: {
    type: 'strength' | 'concern' | 'neutral';
    text: string;
  }[];
  metrics: DDMetric[];
  scoresAtStart: ScoreSnapshot;
  scoresAtEnd: ScoreSnapshot;
  sharedWithStartup?: boolean;
}

/** Icon per objective-metric key (Phase C.1). Falls back to a chart glyph. */
function metricIcon(key: string) {
  switch (key) {
    case 'growth':
    case 'marketGrowth':
      return TrendingUp;
    case 'team':
      return Users;
    case 'runway':
    case 'payback':
      return Clock;
    case 'tam':
    case 'sam':
    case 'som':
    case 'market':
      return Target;
    default:
      return BarChart3;
  }
}

// Sample completed-DD reports for DEMO/MOCK mode only. Rendered exclusively
// behind the `mode.showSampleData` gate below (never a production fallback —
// production reads real `dd_reports` via useCompletedDDReports). This is
// legitimate, intentional demo data per the mock-data principle
// (.planning/MOCK-DATA-REMEDIATION.md) — do NOT delete it; deleting would empty
// the completed-DD list in demo/screenshot mode. (Phase E closeout, 2026-06-15.)
const mockCompletedDDs: CompletedDueDiligence[] = [
  {
    id: 'dd-1',
    startupId: 'startup-1',
    startupName: 'CloudScale AI',
    industry: 'Enterprise SaaS',
    stage: 'Series A',
    decision: 'invest',
    startDate: 'Oct 15, 2024',
    endDate: 'Nov 2, 2024',
    durationDays: 18,
    dataRoomVersion: 'v2.3 (Final)',
    documentsReviewed: 12,
    flaggedIssues: 1,
    finalComments: 'Strong product-market fit with clear path to profitability. Team has relevant enterprise experience.',
    executiveSummary: 'CloudScale AI is building an enterprise-grade AI infrastructure platform that reduces ML deployment time by 80%. The company has demonstrated strong PMF with 180% ARR growth and 120% NRR. The founding team brings deep enterprise sales experience from Salesforce and technical expertise from Google. While customer concentration is a concern, the pipeline suggests diversification is underway.',
    investmentThesis: 'We believe CloudScale is well-positioned to capture the growing enterprise AI infrastructure market ($15B TAM). The combination of technical differentiation, strong unit economics (11.8x LTV:CAC), and experienced team justifies the $40M valuation. The $8M round provides 18+ months runway to hit Series B milestones.',
    keyRisks: 'Customer concentration (top 3 = 40% revenue) remains the primary risk. Additionally, competition from cloud providers (AWS, GCP) could intensify. Mitigation: Strong technical moat and 6-month head start on key features.',
    notablePoints: [
      { type: 'strength', text: 'ARR grew 180% YoY with 120% NRR' },
      { type: 'strength', text: 'Ex-Salesforce founding team with enterprise DNA' },
      { type: 'strength', text: 'Clear technical differentiation vs. competitors' },
      { type: 'concern', text: 'High customer concentration (top 3 = 40%)' },
      { type: 'concern', text: 'Competitive pressure from cloud giants' },
      { type: 'neutral', text: 'Seeking $8M at $40M pre-money valuation' },
    ],
    metrics: [
      { key: 'mrr', label: 'MRR', value: '$2.4M ARR', source: 'profile' },
      { key: 'growth', label: 'Growth', value: '180% YoY', source: 'deck', confidence: 82 },
      { key: 'runway', label: 'Runway', value: '18 months', source: 'deck', confidence: 82 },
      { key: 'team', label: 'Team', value: '24 people', source: 'profile' },
    ],
    scoresAtStart: { overall: 72, confidence: 65, risk: 45 },
    scoresAtEnd: { overall: 84, confidence: 88, risk: 32 },
  },
  {
    id: 'dd-2',
    startupId: 'startup-2',
    startupName: 'FinFlow',
    industry: 'FinTech',
    stage: 'Seed',
    decision: 'pass',
    startDate: 'Sep 5, 2024',
    endDate: 'Sep 20, 2024',
    durationDays: 15,
    dataRoomVersion: 'v1.2',
    documentsReviewed: 8,
    flaggedIssues: 4,
    finalComments: 'Interesting concept but regulatory concerns and unclear path to compliance. Unit economics need work.',
    executiveSummary: 'FinFlow is attempting to build a B2B payments platform for cross-border SMB transactions. While the technical team is strong, the company lacks regulatory expertise and has not secured necessary licenses. Unit economics are challenging with CAC payback exceeding 24 months. Early traction (500 users) is promising but insufficient to validate the model.',
    investmentThesis: 'N/A - Decision to pass. The regulatory uncertainty combined with weak unit economics makes this investment too risky at the current stage. Would reconsider if: (1) regulatory path is cleared, (2) unit economics improve to <12 month payback, (3) Series A lead with fintech expertise commits.',
    keyRisks: 'Regulatory risk is existential - operating without proper licensing could result in shutdown. Unit economics suggest the current pricing/acquisition model is unsustainable. Founder inexperience in regulated industries is a significant concern.',
    notablePoints: [
      { type: 'concern', text: 'No clear regulatory strategy or compliance roadmap' },
      { type: 'concern', text: 'CAC payback > 24 months is unsustainable' },
      { type: 'concern', text: 'Founders lack fintech regulatory experience' },
      { type: 'strength', text: 'Strong technical team (ex-Stripe engineers)' },
      { type: 'strength', text: 'Interesting product vision' },
      { type: 'neutral', text: 'Early traction with 500 users, 45% MoM growth' },
    ],
    metrics: [
      { key: 'mrr', label: 'MRR', value: '$120K ARR', source: 'profile' },
      { key: 'growth', label: 'Growth', value: '45% MoM', source: 'deck', confidence: 60 },
      { key: 'runway', label: 'Runway', value: '8 months', source: 'deck', confidence: 60 },
      { key: 'team', label: 'Team', value: '6 people', source: 'profile' },
    ],
    scoresAtStart: { overall: 58, confidence: 42, risk: 68 },
    scoresAtEnd: { overall: 45, confidence: 72, risk: 78 },
  },
  {
    id: 'dd-3',
    startupId: 'startup-3',
    startupName: 'GreenLogix',
    industry: 'Climate Tech',
    stage: 'Series A',
    decision: 'follow_up',
    startDate: 'Nov 10, 2024',
    endDate: 'Dec 1, 2024',
    durationDays: 21,
    dataRoomVersion: 'v3.0',
    documentsReviewed: 15,
    flaggedIssues: 2,
    finalComments: 'Promising market opportunity. Want to see Q4 numbers and pilot results from enterprise customers.',
    executiveSummary: 'GreenLogix provides supply chain carbon tracking and optimization software for enterprise customers. The market opportunity is substantial ($50B+ TAM) with strong regulatory tailwinds. Three enterprise pilots are in progress with Fortune 500 companies. The main concern is sales cycle length (6-9 months) which impacts capital efficiency.',
    investmentThesis: 'Conditionally positive. If Q4 results show: (1) at least one pilot converting to paid contract, (2) pipeline supporting 3x ARR growth, and (3) clear path to reducing sales cycles, we would be interested in leading or co-leading the round. The climate tech thesis is strong and the team is capable.',
    keyRisks: 'Long enterprise sales cycles (6-9 months) create cash flow challenges and make growth capital-intensive. Regulatory dependence - if carbon reporting mandates are delayed or weakened, demand could soften. Competition from larger ERP vendors entering the space.',
    notablePoints: [
      { type: 'strength', text: 'Large TAM ($50B+) with regulatory tailwinds' },
      { type: 'strength', text: '3 enterprise pilots with Fortune 500 companies' },
      { type: 'strength', text: 'Experienced team with sustainability background' },
      { type: 'concern', text: 'Long sales cycles (6-9 months) impact efficiency' },
      { type: 'concern', text: 'Regulatory dependence for demand drivers' },
      { type: 'neutral', text: 'Awaiting Q4 results and pilot conversions' },
    ],
    metrics: [
      { key: 'mrr', label: 'MRR', value: '$800K ARR', source: 'profile' },
      { key: 'growth', label: 'Growth', value: '95% YoY', source: 'deck', confidence: 70 },
      { key: 'runway', label: 'Runway', value: '14 months', source: 'deck', confidence: 70 },
      { key: 'team', label: 'Team', value: '18 people', source: 'profile' },
    ],
    scoresAtStart: { overall: 68, confidence: 55, risk: 52 },
    scoresAtEnd: { overall: 74, confidence: 68, risk: 48 },
  },
];

type FilterType = 'all' | 'active' | 'completed';

export default function DueDiligenceScreen() {
  useTrackScreen('DueDiligence');
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const [selectedFilter, setSelectedFilter] = useState<FilterType>('all');
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  // C.2 narrative: per-card generate pending + the report being edited.
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [editorReportId, setEditorReportId] = useState<string | null>(null);
  const generateNarrative = useGenerateNarrative();
  const saveNarrative = useSaveNarrative();
  const shareReport = useShareDDReport();
  const { error: showError, confirm: showConfirm } = useAlert();

  const dealflow = useDealflowStore((s) => s.dealflow);
  const user = useProfileStore((s) => s.user);
  // dealflow / access requests key off investor_profiles.id, not the auth uid.
  const { data: investorProfile } = useInvestorProfile(
    mode.showSampleData ? undefined : user?.id,
  );
  // Real persisted DD outcomes (dd_reports), keyed by auth user id. Sample data
  // only in mock/demo mode — never a production fallback.
  const { data: realCompletedDDs = [] } = useCompletedDDReports(user?.id);
  const completedDDs = mode.showSampleData
    ? (realCompletedDDs.length > 0 ? realCompletedDDs : mockCompletedDDs)
    : realCompletedDDs;
  // DD entry is the STAGE (`awaiting_access` or `diligence`). The request rows
  // below only supply the timestamps and the founder's answer — they do not
  // decide membership. This list used to be filtered on `dataRoomAccess`, a
  // column no founder could actually set (RLS blocked their write), so it was
  // permanently empty in production; the column itself is gone as of
  // 20260805160000. Shared with the dashboard via selectDDReviews so the two can
  // never disagree again.
  const { data: myAccessRequests = [] } = useMyDataRoomAccessRequests(
    mode.showSampleData ? undefined : investorProfile?.id,
  );
  const ddReviews = useMemo(
    () => selectDDReviews(dealflow, myAccessRequests),
    [dealflow, myAccessRequests],
  );
  const activeReviews = useMemo(() => ddReviews.map((r) => r.deal), [ddReviews]);
  const accessStateByStartupId = useMemo(
    () => new Map(ddReviews.map((r) => [r.deal.startup.id, r.accessState])),
    [ddReviews],
  );

  // Narrative generate/edit are only meaningful for real (persisted) reports.
  const canEditNarrative = !mode.showSampleData;
  const handleGenerateNarrative = (id: string) => {
    setGeneratingId(id);
    generateNarrative.mutate(id, {
      onError: (e) => showError('Generation failed', getUserMessage(e)),
      onSettled: () => setGeneratingId(null),
    });
  };
  const editorReport = completedDDs.find((d) => d.id === editorReportId) ?? null;
  const editorInitial: DDNarrativeDraft = {
    executiveSummary: editorReport?.executiveSummary ?? '',
    investmentThesis: editorReport?.investmentThesis ?? '',
    keyRisks: editorReport?.keyRisks ?? '',
    notablePoints: editorReport?.notablePoints ?? [],
  };
  const handleSaveNarrative = (draft: DDNarrativeDraft) => {
    if (!editorReportId) return;
    saveNarrative.mutate(
      { ddReportId: editorReportId, narrative: draft },
      {
        onSuccess: () => setEditorReportId(null),
        onError: (e) => showError('Save failed', getUserMessage(e)),
      },
    );
  };
  const handleToggleShare = async (id: string, currentlyShared: boolean) => {
    if (currentlyShared) {
      shareReport.mutate({ ddReportId: id, shared: false });
      return;
    }
    const ok = await showConfirm(
      'Share report with the startup?',
      'The startup will see your full assessment — narrative, decision verdict, and score change — attributed to you. Your private notes and any commitment amount are never shared.',
    );
    if (ok) shareReport.mutate({ ddReportId: id, shared: true });
  };

  // A startup is "concluded" when a real dd_report exists for it. Keyed on the
  // startup_profiles id (the dealflow item's `.startup.id`) — NOT the dealflow
  // row id — to match dd_reports.startup_id. Single source of truth (dd_reports),
  // replacing the legacy ephemeral Zustand conclusions store.
  const hasConcludedReport = (s: (typeof activeReviews)[number]) =>
    realCompletedDDs.some((r) => r.startupId === s.startup.id);

  const activeWithoutConclusion = activeReviews.filter((s) => !hasConcludedReport(s));
  const activeWithConclusion = activeReviews.filter((s) => hasConcludedReport(s));

  const stats = {
    active: activeWithoutConclusion.length,
    completed: completedDDs.length + activeWithConclusion.length,
    invested: completedDDs.filter(d => d.decision === 'invest').length,
    passed: completedDDs.filter(d => d.decision === 'pass').length,
  };

  const filters: { key: FilterType; label: string; count: number }[] = [
    { key: 'all', label: 'All Reviews', count: stats.active + stats.completed },
    { key: 'active', label: 'Active', count: stats.active },
    { key: 'completed', label: 'Completed', count: stats.completed },
  ];

  // `monitor` is the spine's terminal watch stage and the only one a conclusion
  // writes today; `follow`/`follow_up` are its legacy spellings, kept because
  // rows written before the enum was collapsed still carry them.
  const decisionConfig: Record<
    'invest' | 'pass' | 'monitor' | 'follow' | 'follow_up' | 'undecided',
    { color: string; bg: string; label: string; icon: typeof ThumbsUp }
  > = {
    invest: { color: colors.success, bg: colors.traffic.greenLight, label: 'Invested', icon: ThumbsUp },
    pass: { color: colors.error, bg: colors.red.light, label: 'Passed', icon: ThumbsDown },
    monitor: { color: colors.warning, bg: colors.amber.light, label: 'Monitoring', icon: Clock },
    follow: { color: colors.warning, bg: colors.amber.light, label: 'Following Up', icon: Clock },
    follow_up: { color: colors.warning, bg: colors.amber.light, label: 'Following Up', icon: Clock },
    undecided: { color: colors.text.secondary, bg: colors.surface.secondary, label: 'Undecided', icon: Clock },
  };

  const pointTypeConfig = {
    strength: { color: colors.success, bg: colors.traffic.greenLight },
    concern: { color: colors.error, bg: colors.red.light },
    neutral: { color: colors.text.secondary, bg: colors.surface.secondary },
  };

  return (
    <AppLayout title="Due Diligence">
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        {/* Pinned, and carrying the global controls — see the note on score.tsx.
           The usage meter moved into the body: it is descriptive metadata, and a
           navigation row that also has to hold a growing counter is the row that
           squeezes the controls out. No bottomEdge: tab screen. */}
        <ScreenFrame
          eyebrow="Investor Tools"
          title="Due Diligence"
          actions={<ScreenFrame.GlobalActions />}
        >
          <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
            {phoneShell && (
              <View className="px-5 pb-2">
                <UsageIndicator feature="dd_conversations" suffix="/mo" />
              </View>
            )}
            {/* Stats Dashboard */}
            <Animated.View
              entering={FadeInUp.delay(150).duration(500)}
              className="px-5 mb-6"
            >
              <View className="flex-row gap-3">
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{
                    backgroundColor: colors.surface.primary,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.05,
                    shadowRadius: 8,
                    elevation: 2,
                  }}
                >
                  <PlayCircle size={20} color={colors.warning} />
                  <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mt-2">{stats.active}</Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">Active</Text>
                </View>
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{
                    backgroundColor: colors.surface.primary,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.05,
                    shadowRadius: 8,
                    elevation: 2,
                  }}
                >
                  <CheckCircle size={20} color={colors.success} />
                  <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mt-2">{stats.completed}</Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">Completed</Text>
                </View>
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{ backgroundColor: colors.traffic.greenLight }}
                >
                  <ThumbsUp size={20} color={colors.success} />
                  <Text style={{ color: colors.success }} className="text-2xl font-bold mt-2">{stats.invested}</Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">Invested</Text>
                </View>
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{ backgroundColor: colors.red.light }}
                >
                  <ThumbsDown size={20} color={colors.error} />
                  <Text style={{ color: colors.error }} className="text-2xl font-bold mt-2">{stats.passed}</Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">Passed</Text>
                </View>
              </View>
            </Animated.View>

            {/* Section Divider - Filter */}
            <View className="px-5">
              <SectionDivider label="Filter" marginTop={8} marginBottom={12} />
            </View>

            {/* Filters */}
            <Animated.View
              entering={FadeInUp.delay(200).duration(500)}
              className="px-5 mb-6"
            >
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
                <View className="flex-row gap-2">
                  {filters.map((filter) => (
                    <AnimatedPressable
                      key={filter.key}
                      onPress={() => setSelectedFilter(filter.key)}
                      className="px-4 py-2 rounded-full flex-row items-center gap-2"
                      style={{
                        backgroundColor: selectedFilter === filter.key ? colors.primary[600] : colors.surface.primary,
                      }}
                    >
                      <Text
                        className="text-sm font-medium"
                        style={{ color: selectedFilter === filter.key ? 'white' : colors.text.secondary }}
                      >
                        {filter.label}
                      </Text>
                      <View
                        className="px-1.5 py-0.5 rounded-full min-w-[20px] items-center"
                        style={{
                          backgroundColor: selectedFilter === filter.key ? 'rgba(255,255,255,0.2)' : colors.surface.secondary,
                        }}
                      >
                        <Text
                          className="text-xs font-medium"
                          style={{ color: selectedFilter === filter.key ? 'white' : colors.text.tertiary }}
                        >
                          {filter.count}
                        </Text>
                      </View>
                    </AnimatedPressable>
                  ))}
                </View>
              </ScrollView>
            </Animated.View>

            {/* Active Reviews Section */}
            {(selectedFilter === 'all' || selectedFilter === 'active') && activeWithoutConclusion.length > 0 && (
              <Animated.View
                entering={FadeInUp.delay(250).duration(500)}
                className="px-5 mb-6"
              >
                <SectionDivider label="Active Reviews" icon={PlayCircle} marginTop={0} marginBottom={16} />
                <View className="gap-3">
                  {activeWithoutConclusion.map((startup, index) => (
                    <Animated.View key={startup.id} entering={FadeIn.delay(250 + index * 50)}>
                      <AnimatedPressable
                        onPress={() => {
                          track('dd_chat_opened', { startupId: startup.id });
                          router.push(`/duediligence/${startup.id}`);
                        }}
                        className="rounded-2xl overflow-hidden"
                        style={{
                          backgroundColor: colors.surface.primary,
                          shadowColor: '#000',
                          shadowOffset: { width: 0, height: 2 },
                          shadowOpacity: 0.05,
                          shadowRadius: 8,
                          elevation: 2,
                        }}
                      >
                        <View className="p-4">
                          <View className="flex-row items-center justify-between">
                            <View className="flex-row items-center gap-3 flex-1">
                              <View
                                className="w-12 h-12 rounded-xl items-center justify-center"
                                style={{ backgroundColor: `${colors.primary[600]}15` }}
                              >
                                <Text style={{ color: colors.primary[600] }} className="text-lg font-bold">
                                  {startup.startup.company.name.charAt(0)}
                                </Text>
                              </View>
                              <View className="flex-1">
                                <Text numberOfLines={1} style={{ color: colors.text.primary }} className="font-semibold text-base">{startup.startup.company.name}</Text>
                                <Text numberOfLines={1} style={{ color: colors.text.tertiary }} className="text-sm">
                                  {startup.startup.company.industry} • {startup.startup.company.stage}
                                </Text>
                              </View>
                            </View>
                            <View className="flex-row items-center gap-2">
                              {(() => {
                                // Badge tells the truth about the data-room request:
                                // awaiting the founder, granted, or declined. A
                                // declined review deliberately STAYS in the list —
                                // dropping it from the pipeline is the investor's call.
                                const badge = ddAccessBadge(
                                  accessStateByStartupId.get(startup.startup.id) ?? 'granted',
                                  colors,
                                );
                                return (
                                  <View
                                    className="px-3 py-1.5 rounded-full flex-row items-center gap-1.5"
                                    style={{ backgroundColor: badge.bg }}
                                  >
                                    <View className="w-2 h-2 rounded-full" style={{ backgroundColor: badge.color }} />
                                    <Text numberOfLines={1} style={{ color: badge.color }} className="text-xs font-medium">{badge.label}</Text>
                                  </View>
                                );
                              })()}
                              <ChevronRight size={20} color={colors.text.tertiary} />
                            </View>
                          </View>
                        </View>
                      </AnimatedPressable>
                    </Animated.View>
                  ))}
                </View>
              </Animated.View>
            )}

            {/* Empty State for Active */}
            {selectedFilter === 'active' && activeWithoutConclusion.length === 0 && (
              <Animated.View
                entering={FadeInUp.delay(250).duration(500)}
                className="px-5 mb-6"
              >
                <View
                  className="rounded-2xl p-8 items-center"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  <Folder size={48} color={colors.text.tertiary} />
                  <Text style={{ color: colors.text.secondary }} className="text-center mt-4 text-base">
                    No active reviews
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-center mt-1 text-sm">
                    Start due diligence on a deal in your dealflow to open a review
                  </Text>
                </View>
              </Animated.View>
            )}

            {/* Completed Reviews Section */}
            {(selectedFilter === 'all' || selectedFilter === 'completed') && completedDDs.length > 0 && (
              <Animated.View
                entering={FadeInUp.delay(300).duration(500)}
                className="px-5 mb-6"
              >
                <SectionDivider label="Completed Reviews" icon={History} marginTop={0} marginBottom={16} />
                <View className="gap-4">
                  {completedDDs.map((dd, index) => {
                    const decision = decisionConfig[dd.decision];
                    const DecisionIcon = decision.icon;
                    const isExpanded = expandedCard === dd.id;
                    // Narrow optional rich fields with defaults so the JSX below stays readable
                    const metrics: DDMetric[] = dd.metrics ?? [];
                    const scoresAtStart = dd.scoresAtStart ?? { overall: 0, confidence: 0, risk: 0 };
                    const scoresAtEnd = dd.scoresAtEnd ?? { overall: 0, confidence: 0, risk: 0 };
                    // Canonical IRS band for the overall score (boundaries 81/66/46/26),
                    // replacing the legacy 70/50 traffic-light. DD runs on demo data, so
                    // the band is derived from the number (no score_event here).
                    const startBand = scoreToLabel(scoresAtStart.overall);
                    const startTint = labelColor(startBand);
                    const endBand = scoreToLabel(scoresAtEnd.overall);
                    const endTint = labelColor(endBand);
                    const notablePoints = dd.notablePoints ?? [];

                    return (
                      <Animated.View key={dd.id} entering={FadeIn.delay(300 + index * 80)}>
                        <AnimatedPressable
                          onPress={() => setExpandedCard(isExpanded ? null : dd.id)}
                          className="rounded-2xl overflow-hidden"
                          style={{
                            backgroundColor: colors.surface.primary,
                            shadowColor: '#000',
                            shadowOffset: { width: 0, height: 2 },
                            shadowOpacity: 0.05,
                            shadowRadius: 8,
                            elevation: 2,
                          }}
                        >
                          <View className="p-4">
                            {/* Header */}
                            <View className="flex-row items-start justify-between mb-3">
                              <View className="flex-row items-center gap-3 flex-1">
                                <View
                                  className="w-12 h-12 rounded-xl items-center justify-center"
                                  style={{ backgroundColor: `${colors.primary[600]}15` }}
                                >
                                  <Text style={{ color: colors.primary[600] }} className="text-lg font-bold">
                                    {dd.startupName.charAt(0)}
                                  </Text>
                                </View>
                                <View className="flex-1">
                                  <Text style={{ color: colors.text.primary }} className="font-semibold text-base">{dd.startupName}</Text>
                                  <Text style={{ color: colors.text.tertiary }} className="text-sm">
                                    {dd.industry} • {dd.stage}
                                  </Text>
                                </View>
                              </View>
                              <View
                                className="px-3 py-1.5 rounded-full flex-row items-center gap-1.5"
                                style={{ backgroundColor: decision.bg }}
                              >
                                <DecisionIcon size={14} color={decision.color} />
                                <Text style={{ color: decision.color }} className="text-xs font-semibold">{decision.label}</Text>
                              </View>
                            </View>

                            {/* Date Range & Data Room summary (real doc count · date) */}
                            <View className="flex-row items-center gap-4 mb-3">
                              <View className="flex-row items-center gap-1.5">
                                <Calendar size={14} color={colors.text.tertiary} />
                                <Text style={{ color: colors.text.tertiary }} className="text-xs">
                                  {dd.startDate} → {dd.endDate}
                                </Text>
                              </View>
                              {!!dd.dataRoomVersion && (
                                <View className="flex-row items-center gap-1.5 flex-shrink" >
                                  <Folder size={14} color={colors.text.tertiary} />
                                  <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                                    {dd.dataRoomVersion}
                                  </Text>
                                </View>
                              )}
                            </View>

                            {/* Quick Metrics — first few real metrics */}
                            {metrics.length > 0 && (
                              <View className="flex-row flex-wrap gap-2 mb-3">
                                {metrics.slice(0, 3).map((m) => (
                                  <View key={m.key} className="px-2 py-1 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
                                    <Text style={{ color: colors.text.secondary }} className="text-xs" numberOfLines={1}>{m.value}</Text>
                                  </View>
                                ))}
                              </View>
                            )}

                            {/* Final Comments Preview */}
                            <Text style={{ color: colors.text.secondary }} className="text-sm" numberOfLines={isExpanded ? undefined : 2}>
                              "{dd.finalComments}"
                            </Text>

                            {/* Expanded Content */}
                            {isExpanded && (
                              <Animated.View entering={FadeIn.duration(200)} className="mt-4 pt-4 border-t" style={{ borderColor: colors.border.light }}>

                                {/* Score Comparison - Start vs End */}
                                <Text style={{ color: colors.text.primary }} className="font-semibold mb-3">Score Evolution</Text>
                                <View className="rounded-xl p-4 mb-4" style={{ backgroundColor: colors.surface.secondary }}>
                                  <View className="flex-row items-center justify-between mb-3">
                                    <Text style={{ color: colors.text.tertiary }} className="text-xs">At DD Start ({dd.startDate})</Text>
                                    <Text style={{ color: colors.text.tertiary }} className="text-xs">At DD End ({dd.endDate})</Text>
                                  </View>

                                  {/* Overall Score — canonical IRS bands */}
                                  <View className="flex-row items-center justify-between mb-3">
                                    <View className="flex-row items-center gap-2">
                                      <View className="w-10 h-10 rounded-lg items-center justify-center" style={{ backgroundColor: `${startTint}1A` }}>
                                        <Text style={{ color: startTint }} className="font-bold">{scoresAtStart.overall}</Text>
                                      </View>
                                      <Text style={{ color: colors.text.secondary }} className="text-xs">Overall</Text>
                                    </View>
                                    <View className="flex-1 mx-4 h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.surface.primary }}>
                                      <View
                                        className="h-full rounded-full"
                                        style={{
                                          backgroundColor: scoresAtEnd.overall >= scoresAtStart.overall ? colors.success : colors.error,
                                          width: '100%'
                                        }}
                                      />
                                    </View>
                                    <View className="flex-row items-center gap-2">
                                      <View className="items-end">
                                        <Text style={{ color: colors.text.secondary }} className="text-xs">Overall</Text>
                                        <Text style={{ color: endTint }} className="text-[10px] font-semibold" numberOfLines={1}>{capitalizeLabel(endBand)}</Text>
                                      </View>
                                      <View className="w-10 h-10 rounded-lg items-center justify-center" style={{ backgroundColor: `${endTint}1A` }}>
                                        <Text style={{ color: endTint }} className="font-bold">{scoresAtEnd.overall}</Text>
                                      </View>
                                    </View>
                                  </View>

                                  {/* Confidence Score */}
                                  <View className="flex-row items-center justify-between mb-3">
                                    <View className="flex-row items-center gap-2">
                                      <View className="w-10 h-10 rounded-lg items-center justify-center" style={{ backgroundColor: colors.surface.primary }}>
                                        <Text style={{ color: colors.text.primary }} className="font-bold text-sm">{scoresAtStart.confidence}</Text>
                                      </View>
                                      <Text style={{ color: colors.text.secondary }} className="text-xs">Confidence</Text>
                                    </View>
                                    <View className="flex-1 mx-4 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: colors.surface.primary }}>
                                      <View
                                        className="h-full rounded-full"
                                        style={{
                                          backgroundColor: colors.info,
                                          width: `${scoresAtEnd.confidence}%`
                                        }}
                                      />
                                    </View>
                                    <View className="flex-row items-center gap-2">
                                      <Text style={{ color: colors.text.secondary }} className="text-xs">Confidence</Text>
                                      <View className="w-10 h-10 rounded-lg items-center justify-center" style={{ backgroundColor: `${colors.info}20` }}>
                                        <Text style={{ color: colors.info }} className="font-bold text-sm">{scoresAtEnd.confidence}</Text>
                                      </View>
                                    </View>
                                  </View>

                                  {/* Risk Score */}
                                  <View className="flex-row items-center justify-between">
                                    <View className="flex-row items-center gap-2">
                                      <View className="w-10 h-10 rounded-lg items-center justify-center" style={{ backgroundColor: colors.surface.primary }}>
                                        <Text style={{ color: colors.text.primary }} className="font-bold text-sm">{scoresAtStart.risk}</Text>
                                      </View>
                                      <Text style={{ color: colors.text.secondary }} className="text-xs">Risk</Text>
                                    </View>
                                    <View className="flex-1 mx-4 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: colors.surface.primary }}>
                                      <View
                                        className="h-full rounded-full"
                                        style={{
                                          backgroundColor: scoresAtEnd.risk <= 40 ? colors.success : scoresAtEnd.risk <= 60 ? colors.warning : colors.error,
                                          width: `${scoresAtEnd.risk}%`
                                        }}
                                      />
                                    </View>
                                    <View className="flex-row items-center gap-2">
                                      <Text style={{ color: colors.text.secondary }} className="text-xs">Risk</Text>
                                      <View className="w-10 h-10 rounded-lg items-center justify-center" style={{ backgroundColor: scoresAtEnd.risk <= 40 ? colors.traffic.greenLight : scoresAtEnd.risk <= 60 ? colors.amber.light : colors.red.light }}>
                                        <Text style={{ color: scoresAtEnd.risk <= 40 ? colors.success : scoresAtEnd.risk <= 60 ? colors.warning : colors.error }} className="font-bold text-sm">{scoresAtEnd.risk}</Text>
                                      </View>
                                    </View>
                                  </View>

                                  {/* Score Change Summary */}
                                  <View className="flex-row justify-center mt-3 pt-3 border-t" style={{ borderColor: colors.border.light }}>
                                    <Text style={{ color: scoresAtEnd.overall > scoresAtStart.overall ? colors.success : scoresAtEnd.overall < scoresAtStart.overall ? colors.error : colors.text.tertiary }} className="text-xs font-medium">
                                      {scoresAtEnd.overall > scoresAtStart.overall
                                        ? `↑ Overall improved by ${scoresAtEnd.overall - scoresAtStart.overall} points during DD`
                                        : scoresAtEnd.overall < scoresAtStart.overall
                                        ? `↓ Overall decreased by ${scoresAtStart.overall - scoresAtEnd.overall} points during DD`
                                        : 'No change in overall score during DD'}
                                    </Text>
                                  </View>
                                </View>

                                {/* Narrative (LLM-generated, Phase C.2). Render only what exists;
                                    show one honest affordance when no narrative is present yet. */}
                                {(() => {
                                  const hasNarrative =
                                    !!dd.executiveSummary || !!dd.investmentThesis || !!dd.keyRisks || notablePoints.length > 0;
                                  if (!hasNarrative) {
                                    const generating = generateNarrative.isPending && generatingId === dd.id;
                                    return (
                                      <View className="rounded-xl p-3 mb-4" style={{ backgroundColor: colors.surface.secondary }}>
                                        <Text style={{ color: colors.text.tertiary }} className="text-sm leading-relaxed mb-3">
                                          Narrative summary (executive summary, thesis, key risks) is not yet generated for this report.
                                        </Text>
                                        {canEditNarrative && (
                                          <GenerateNarrativeAction
                                            reportId={dd.id}
                                            startupId={dd.startupId}
                                            startupName={dd.startupName}
                                            dealId={dealflow.find((d) => d.startup.id === dd.startupId)?.id ?? null}
                                            generating={generating}
                                            onGenerate={() => handleGenerateNarrative(dd.id)}
                                          />
                                        )}
                                      </View>
                                    );
                                  }
                                  return (
                                    <>
                                      {!!dd.executiveSummary && (
                                        <>
                                          <Text style={{ color: colors.text.primary }} className="font-semibold mb-2">Executive Summary</Text>
                                          <Text style={{ color: colors.text.secondary }} className="text-sm mb-4 leading-relaxed">{dd.executiveSummary}</Text>
                                        </>
                                      )}
                                      {!!dd.investmentThesis && (
                                        <>
                                          <Text style={{ color: colors.text.primary }} className="font-semibold mb-2">Investment Thesis</Text>
                                          <View className="rounded-xl p-3 mb-4" style={{ backgroundColor: decision.bg }}>
                                            <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">{dd.investmentThesis}</Text>
                                          </View>
                                        </>
                                      )}
                                      {!!dd.keyRisks && (
                                        <>
                                          <Text style={{ color: colors.text.primary }} className="font-semibold mb-2">Key Risks Identified</Text>
                                          <View className="rounded-xl p-3 mb-4" style={{ backgroundColor: colors.red.light }}>
                                            <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">{dd.keyRisks}</Text>
                                          </View>
                                        </>
                                      )}
                                      {notablePoints.length > 0 && (
                                        <>
                                          <Text style={{ color: colors.text.primary }} className="font-semibold mb-3">Notable Points</Text>
                                          <View className="gap-2 mb-4">
                                            {notablePoints.map((point, idx) => {
                                              const pointConfig = pointTypeConfig[point.type];
                                              return (
                                                <View key={idx} className="flex-row items-start gap-2 p-2 rounded-lg" style={{ backgroundColor: `${pointConfig.color}10` }}>
                                                  <View className="w-2 h-2 rounded-full mt-1.5" style={{ backgroundColor: pointConfig.color }} />
                                                  <Text style={{ color: colors.text.secondary }} className="text-sm flex-1">{point.text}</Text>
                                                </View>
                                              );
                                            })}
                                          </View>
                                        </>
                                      )}
                                      {canEditNarrative && (
                                        <View className="flex-row flex-wrap gap-2">
                                          <View style={{ borderRadius: 8, overflow: 'hidden', backgroundColor: colors.surface.secondary }}>
                                            <AnimatedPressable
                                              onPress={() => setEditorReportId(dd.id)}
                                              style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 8 }}
                                            >
                                              <Pencil size={13} color={colors.primary[600]} />
                                              <Text style={{ color: colors.primary[600], fontSize: 12, fontWeight: '600' }}>Edit narrative</Text>
                                            </AnimatedPressable>
                                          </View>
                                          {(() => {
                                            const shared = !!dd.sharedWithStartup;
                                            return (
                                              <View style={{ borderRadius: 8, overflow: 'hidden', backgroundColor: shared ? colors.traffic.greenLight : colors.primary[600] }}>
                                                <AnimatedPressable
                                                  onPress={() => handleToggleShare(dd.id, shared)}
                                                  style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 8 }}
                                                >
                                                  {shared ? <Check size={13} color={colors.success} /> : <Share2 size={13} color="#fff" />}
                                                  <Text style={{ color: shared ? colors.success : '#fff', fontSize: 12, fontWeight: '600' }}>
                                                    {shared ? 'Shared with startup' : 'Share with startup'}
                                                  </Text>
                                                </AnimatedPressable>
                                              </View>
                                            );
                                          })()}
                                        </View>
                                      )}
                                    </>
                                  );
                                })()}

                                {/* Review Stats */}
                                <View className="flex-row gap-3 mb-4">
                                  <View className="flex-1 p-3 rounded-xl" style={{ backgroundColor: colors.surface.secondary }}>
                                    <View className="flex-row items-center gap-2 mb-1">
                                      <FileText size={14} color={colors.text.tertiary} />
                                      <Text style={{ color: colors.text.tertiary }} className="text-xs">Documents</Text>
                                    </View>
                                    <Text style={{ color: colors.text.primary }} className="font-semibold">{dd.documentsReviewed} reviewed</Text>
                                  </View>
                                  <View className="flex-1 p-3 rounded-xl" style={{ backgroundColor: colors.surface.secondary }}>
                                    <View className="flex-row items-center gap-2 mb-1">
                                      <Clock size={14} color={colors.text.tertiary} />
                                      <Text style={{ color: colors.text.tertiary }} className="text-xs">Duration</Text>
                                    </View>
                                    <Text style={{ color: colors.text.primary }} className="font-semibold">{dd.durationDays} days</Text>
                                  </View>
                                  {(() => {
                                    const flagged = dd.flaggedIssues ?? 0;
                                    return (
                                      <View className="flex-1 p-3 rounded-xl" style={{ backgroundColor: flagged > 0 ? colors.red.light : colors.traffic.greenLight }}>
                                        <View className="flex-row items-center gap-2 mb-1">
                                          <AlertTriangle size={14} color={flagged > 0 ? colors.error : colors.success} />
                                          <Text style={{ color: flagged > 0 ? colors.error : colors.success }} className="text-xs">Issues</Text>
                                        </View>
                                        <Text style={{ color: flagged > 0 ? colors.error : colors.success }} className="font-semibold">
                                          {flagged > 0 ? `${flagged} flagged` : 'None'}
                                        </Text>
                                      </View>
                                    );
                                  })()}
                                </View>

                                {/* Company Snapshot — real metrics with provenance (Phase C.1) */}
                                {metrics.length > 0 && (
                                  <>
                                    <Text style={{ color: colors.text.primary }} className="font-semibold mb-3">Company Snapshot (at time of review)</Text>
                                    <View className="flex-row flex-wrap gap-2">
                                      {metrics.map((m) => {
                                        const MetricIcon = metricIcon(m.key);
                                        return (
                                          <View key={m.key} className="flex-row items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
                                            <MetricIcon size={14} color={m.source === 'deck' ? colors.primary[600] : colors.text.tertiary} />
                                            <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>{m.label}</Text>
                                            <Text style={{ color: colors.text.secondary }} className="text-sm font-medium" numberOfLines={1}>{m.value}</Text>
                                          </View>
                                        );
                                      })}
                                    </View>
                                    {/* Provenance legend — honest about where each number came from */}
                                    {(() => {
                                      const hasDeck = metrics.some((m) => m.source === 'deck');
                                      const hasProfile = metrics.some((m) => m.source === 'profile');
                                      const parts: string[] = [];
                                      if (hasProfile) parts.push('founder-reported');
                                      if (hasDeck) parts.push('extracted from deck');
                                      return (
                                        <Text style={{ color: colors.text.tertiary }} className="text-[10px] mt-2">
                                          Sources: {parts.join(' · ')}
                                        </Text>
                                      );
                                    })()}
                                  </>
                                )}
                              </Animated.View>
                            )}

                            {/* Expand/Collapse Indicator */}
                            <View className="flex-row justify-center mt-3">
                              <Text style={{ color: colors.primary[600] }} className="text-xs font-medium">
                                {isExpanded ? 'Show less' : 'View full report'}
                              </Text>
                            </View>
                          </View>
                        </AnimatedPressable>
                      </Animated.View>
                    );
                  })}
                </View>
              </Animated.View>
            )}

            {/* Empty State for Completed */}
            {(selectedFilter === 'completed' || selectedFilter === 'all') && completedDDs.length === 0 && activeWithConclusion.length === 0 && (
              <Animated.View
                entering={FadeInUp.delay(300).duration(500)}
                className="px-5 mb-6"
              >
                <View
                  className="rounded-2xl p-8 items-center"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  <CheckCircle size={48} color={colors.text.tertiary} />
                  <Text style={{ color: colors.text.secondary }} className="text-center mt-4 text-base">
                    No completed reviews yet
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-center mt-1 text-sm">
                    Complete your first due diligence to see it here
                  </Text>
                </View>
              </Animated.View>
            )}

            {/* Section Divider - Insights */}
            <View className="px-5">
              <SectionDivider label="Insights" marginTop={16} marginBottom={12} />
            </View>

            {/* Insights Summary */}
            <Animated.View
              entering={FadeInUp.delay(400).duration(500)}
              className="px-5 pb-8"
            >
              <View
                className="rounded-2xl p-4"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <View className="flex-row items-center gap-2 mb-3">
                  <BarChart3 size={18} color={colors.primary[600]} />
                  <Text style={{ color: colors.text.primary }} className="font-semibold">Review Insights</Text>
                </View>
                <View className="flex-row gap-4">
                  <View className="flex-1">
                    <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">Conversion Rate</Text>
                    <Text style={{ color: colors.success }} className="text-lg font-bold">
                      {stats.completed > 0 ? Math.round((stats.invested / stats.completed) * 100) : 0}%
                    </Text>
                    <Text style={{ color: colors.text.tertiary }} className="text-xs">Reviews → Investment</Text>
                  </View>
                  <View className="flex-1">
                    <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">Avg. Review Time</Text>
                    <Text style={{ color: colors.text.primary }} className="text-lg font-bold">14 days</Text>
                    <Text style={{ color: colors.text.tertiary }} className="text-xs">Per startup</Text>
                  </View>
                  <View className="flex-1">
                    <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">Total Reviewed</Text>
                    <Text style={{ color: colors.text.primary }} className="text-lg font-bold">{stats.active + stats.completed}</Text>
                    <Text style={{ color: colors.text.tertiary }} className="text-xs">Startups</Text>
                  </View>
                </View>
              </View>
            </Animated.View>
          </ScrollView>
        </ScreenFrame>
      </View>

      <DDNarrativeEditor
        visible={editorReportId !== null && canEditNarrative}
        onClose={() => setEditorReportId(null)}
        initial={editorInitial}
        onSave={handleSaveNarrative}
        saving={saveNarrative.isPending}
      />
    </AppLayout>
  );
}
